import { useMemo } from 'react'
import { z } from 'zod'
import { useSyncedState } from '~/state/useSyncedState'
import { usePageControls } from '~/api/hooks/useCharts'
import {
  CAMPAIGN_EXACT_DIM,
  predicateOf,
  resolvedCampaignFilters,
  resolvedRatioCampaignFilters,
} from './campaignPredicate'
import type { CampaignTimeslotItem, Controls } from '~/api/endpoints/getControls'

export interface ActiveCampaign {
  id: string
  label: string
  from: string
  to: string
  predicate: Record<string, string[]>
  item: CampaignTimeslotItem
  /** C3 (spec D-C3-1): true iff the active fact's dim catalog (dims_view) carries the exact
   *  `Campaign` dim — rows are tagged with campaign identity directly, so the campaign resolves
   *  to `{Campaign:[id]}` rather than intersected attribution filters. */
  factCarriesCampaign: boolean
  /** C3: resolvedCampaignFilters(item, factCarriesCampaign), catalog-pruned to only the dims the
   *  active fact's dims_view actually carries — the exact request-level filter set a campaign
   *  arm contributes. This is what mergeArmFilters merges into baseParams.filter_dims. */
  resolvedFilters: Record<string, string[]>
}

/** C3 (spec D-C3-1): the union of dim names a fact's controls carry — read from dims_view (the
 *  per-fact dim-combinations MV), the same source useFilterApplicability/filterApplicability.ts
 *  already trust for "does this fact carry dim X". `dims` is nullable per the controls schema (a
 *  Frame row's NULLs) — guard, don't assume every combo row has it. Exported so every per-fact
 *  carrying-flag caller (this hook for the page's own fact, useMultiTfTimeframes' arm resolver,
 *  the driver-arm tile sites for the driver fact) shares the ONE trusted source instead of
 *  re-deriving the union themselves. */
export function dimNamesOf(controlsData: Controls | undefined): Set<string> {
  const dimNames = new Set<string>()
  for (const combo of controlsData?.dims_view ?? []) {
    for (const k of Object.keys(combo.dims ?? {})) dimNames.add(k)
  }
  return dimNames
}

/** Campaigns C1 (spec §4): resolve the `campaign` URL param against the fact's controls.
 *  Null while unset/unresolved — callers treat null as "no campaign context".
 *
 *  Campaigns C3b (spec D-C3b-1): when `ratio` is set (a walk_ratio KPI), the campaign resolves off
 *  the PAIR of component facts, not the single page/registry fact. A ratio is a two-fact
 *  computation, so the exact `{Campaign:[id]}` form is only valid when BOTH facts carry the dim;
 *  otherwise attribution filters on both sides. Never mixed — one-sided exact is the D0 defect
 *  (sales_cpo's numerator carries Campaign, its denominator does not → the DB scoped one side only,
 *  a wrong number in production). `ratio` null/undefined → byte-identical to the single-fact path.
 *  The component-controls hooks are unconditional and null-gated (usePageControls is `enabled`-
 *  gated), so they fire no query on the non-ratio path. */
export function useCampaignState(
  factTable: string,
  ratio?: { numFact: string; denomFact: string } | null,
): ActiveCampaign | null {
  const [id] = useSyncedState('campaign', z.string(), 'url', '')
  const controls = usePageControls(factTable)
  const numFact = ratio?.numFact ?? null
  const denomFact = ratio?.denomFact ?? null
  const numControls = usePageControls(numFact)
  const denomControls = usePageControls(denomFact)
  return useMemo(() => {
    if (!id) return null
    const item = controls.data?.campaign_timeslots?.items.find((i) => i.value === id)
    if (!item || !item.from || !item.to) return null

    // C3b: for a ratio, carrying-detection AND the catalog prune both read the UNION of the two
    // component facts' dims_view — never the page fact's. `dimNames`/`factCarriesCampaign`/`raw`
    // collapse to the single-fact C3 rule when `ratio` is unset, keeping that path byte-identical.
    const ratioActive = numFact !== null && denomFact !== null
    const numDims = ratioActive ? dimNamesOf(numControls.data) : null
    const denomDims = ratioActive ? dimNamesOf(denomControls.data) : null

    const dimNames = ratioActive
      ? new Set<string>([...numDims!, ...denomDims!])
      : dimNamesOf(controls.data)
    const factCarriesCampaign = ratioActive
      ? numDims!.has(CAMPAIGN_EXACT_DIM) && denomDims!.has(CAMPAIGN_EXACT_DIM)
      : dimNames.has(CAMPAIGN_EXACT_DIM)
    const raw = ratioActive
      ? resolvedRatioCampaignFilters(
          item,
          numDims!.has(CAMPAIGN_EXACT_DIM),
          denomDims!.has(CAMPAIGN_EXACT_DIM),
        )
      : resolvedCampaignFilters(item, factCarriesCampaign)
    // Catalog-prune: only dims the active fact(s) actually carry reach the walk — preserves the
    // pre-C3 request-level behavior (an attribution dim absent from the fact never reaches it).
    const resolvedFilters = Object.fromEntries(
      Object.entries(raw).filter(([dim]) => dimNames.has(dim)),
    )
    return {
      id,
      label: item.label,
      from: item.from,
      to: item.to,
      predicate: predicateOf(item),
      item,
      factCarriesCampaign,
      resolvedFilters,
    }
  }, [id, numFact, denomFact, controls.data, numControls.data, denomControls.data])
}
